/**
 * The arithmetic behind the chart, kept separate from the SVG so the
 * uneven-spacing and degenerate-series handling can be read (and reasoned
 * about) without wading through markup.
 */
import type { SeriesPoint } from './types.js';

export interface Domain {
  min: number;
  max: number;
}

/** The x position derives from the timestamp, never from array index —
 *  reused everywhere a point needs a time. */
export function toTime(point: Pick<SeriesPoint, 'recordedAt'>): number {
  return new Date(point.recordedAt).getTime();
}

/** Sorted ascending by timestamp. The API is expected to already return
 *  points in order, but the chart never assumes it — an out-of-order point
 *  would otherwise draw a line that whips backwards through time. */
export function sortByTime<T extends Pick<SeriesPoint, 'recordedAt'>>(points: readonly T[]): T[] {
  return [...points].sort((a, b) => toTime(a) - toTime(b));
}

export function timeDomain(points: readonly Pick<SeriesPoint, 'recordedAt'>[]): [number, number] {
  let min = Infinity;
  let max = -Infinity;
  for (const p of points) {
    const t = toTime(p);
    if (t < min) min = t;
    if (t > max) max = t;
  }
  if (!Number.isFinite(min) || !Number.isFinite(max)) return [0, 1];
  return [min, max];
}

/**
 * How much of the axis the readings may give up so a goal line fits.
 *
 * At 2, the readings always keep at least half the height. Beyond that the
 * chart stops being about what you weigh and becomes a picture of how far
 * away the target is — which the words under it already say, and say better.
 */
const MAX_AXIS_STRETCH_FOR_GOAL = 2;

/**
 * The y-domain. Three dangers, all handled here rather than left to an
 * auto-scale:
 *  - A `suggestedAxis` outside the data's own range must widen the domain,
 *    never get clipped off the plot.
 *  - A flat or near-flat series (all-identical values, or noise inside a
 *    single unit of precision) must not have its tiny real range blown up
 *    into a dramatic-looking line. A minimum span, tied to the node's own
 *    reporting precision (not to the data), puts a floor under how far the
 *    axis is allowed to zoom in.
 *  - A DISTANT goal must not flatten the readings. This used to widen the
 *    domain unconditionally, and with 26 kg still to go it made every real
 *    day's half-kilo about two percent of the axis — a straight line where
 *    there was steady progress. The goal now only joins the axis when the
 *    readings keep enough of it to still be readable; when it does not fit,
 *    the caller marks it at the edge instead, and the line comes into view
 *    on its own as the readings approach it.
 */
export function buildValueDomain(options: {
  values: readonly number[];
  precision: number;
  suggestedAxis?: { min: number; max: number };
  goalThreshold?: number;
}): Domain {
  const { values, precision, suggestedAxis, goalThreshold } = options;
  let min = suggestedAxis?.min ?? Infinity;
  let max = suggestedAxis?.max ?? -Infinity;
  for (const v of values) {
    if (v < min) min = v;
    if (v > max) max = v;
  }
  if (!Number.isFinite(min) || !Number.isFinite(max)) {
    return { min: 0, max: 1 };
  }

  // The floor: at this node's own precision, ten steps' worth of range.
  // For a kg-at-1-decimal weight that is 1 kg, not the 0.1 kg an auto-scale
  // would otherwise zoom into for an all-identical series.
  const minSpan = 10 * 10 ** -precision;
  if (max - min < minSpan) {
    const mid = (min + max) / 2;
    min = mid - minSpan / 2;
    max = mid + minSpan / 2;
  }

  // The goal joins the axis only if the readings keep their share of it.
  if (goalThreshold !== undefined) {
    const readingSpan = max - min;
    const withGoal = {
      min: Math.min(min, goalThreshold),
      max: Math.max(max, goalThreshold),
    };
    if (withGoal.max - withGoal.min <= readingSpan * MAX_AXIS_STRETCH_FOR_GOAL) {
      min = withGoal.min;
      max = withGoal.max;
    }
  }

  const pad = (max - min) * 0.1;
  return { min: min - pad, max: max + pad };
}

/** Classic Heckbert "nice numbers": round tick spacing instead of whatever
 *  the domain's own span happens to divide into. */
function niceNum(range: number, round: boolean): number {
  if (range <= 0) return 1;
  const exponent = Math.floor(Math.log10(range));
  const fraction = range / 10 ** exponent;
  // A tiny epsilon so a fraction that lands on a boundary only by floating-
  // point noise (e.g. 2.9999999999999996 instead of 3) doesn't fall into the
  // stricter bucket and produce a visibly denser grid than intended.
  const EPS = 1e-9;
  let niceFraction: number;
  if (round) {
    if (fraction < 1.5 - EPS) niceFraction = 1;
    else if (fraction < 3 - EPS) niceFraction = 2;
    else if (fraction < 7 - EPS) niceFraction = 5;
    else niceFraction = 10;
  } else {
    if (fraction <= 1) niceFraction = 1;
    else if (fraction <= 2) niceFraction = 2;
    else if (fraction <= 5) niceFraction = 5;
    else niceFraction = 10;
  }
  return niceFraction * 10 ** exponent;
}

/** Evenly spaced, round-numbered gridline values that fully cover `domain`
 *  (the caller should union its plotting scale with these, so a tick never
 *  falls outside the drawn axis). */
export function buildTicks(domain: Domain, targetCount = 5): number[] {
  const span = domain.max - domain.min;
  if (span <= 0) return [domain.min];
  const step = niceNum(span / Math.max(1, targetCount - 1), true);
  const niceMin = Math.floor(domain.min / step) * step;
  const niceMax = Math.ceil(domain.max / step) * step;
  const ticks: number[] = [];
  for (let v = niceMin; v <= niceMax + step / 2; v += step) {
    // Round away the float noise that a repeated += step accumulates.
    ticks.push(Math.round(v / step) * step);
  }
  return ticks;
}

/** How much a single week gained or lost, measured against ITSELF only —
 *  its own last reading minus its own first, never a diff against a
 *  neighbouring week's average. Only meaningful for a node whose catalog
 *  `aggregation` is `'mean'`; a `'sum'` node's weekly buckets are already
 *  totals, so a further first-to-last diff inside one is a second-order
 *  figure nobody asked for. Gating that is the caller's (the screen's) job,
 *  not this function's. */
export interface WeeklyChange {
  /** ISO-8601, the UTC Monday of the week this belongs to. */
  weekStart: string;
  /** last - first, INSIDE this week. Negative is a loss. Never NaN. */
  change: number;
  first: number;
  last: number;
  /** When the first and last reading of this week were actually taken.
   *  "0.6 kg lost this week" is a different fact across six days than
   *  across two, and the bar alone cannot say which. */
  firstAt: string;
  lastAt: string;
  /** Readings behind it. */
  count: number;
  /** True while this week is still running: the change is real but partial. */
  inProgress: boolean;
}

const WEEK_MS = 7 * 24 * 60 * 60 * 1000;

/**
 * Turns weekly buckets into the change inside each one. A bucket with no
 * `first`/`last` — an unaggregated reading, or an older API response that
 * predates them — is skipped rather than defaulted to zero: a bar claiming
 * "no change" is a claim, and one we cannot make from a single point. A
 * bucket whose `count` is 1 is skipped for the same reason — one reading has
 * no inside to have changed across, so `first === last` there would produce
 * a zero indistinguishable from a genuinely flat week.
 *
 * `now` is injectable so the in-progress week can be tested.
 */
export function weeklyChanges(points: readonly SeriesPoint[], now: Date = new Date()): WeeklyChange[] {
  const nowMs = now.getTime();
  const result: WeeklyChange[] = [];
  for (const point of sortByTime(points)) {
    if (point.first === undefined || point.last === undefined) continue;
    // An older API that predates the timestamps is skipped rather than
    // guessed at: attributing a change to the wrong days is worse than
    // leaving the week out.
    if (point.firstAt === undefined || point.lastAt === undefined) continue;
    if (point.count === 1) continue;
    let change = point.last - point.first;
    if (Object.is(change, -0)) change = 0;
    const weekStartMs = toTime(point);
    result.push({
      weekStart: point.recordedAt,
      change,
      first: point.first,
      last: point.last,
      firstAt: point.firstAt,
      lastAt: point.lastAt,
      count: point.count,
      inProgress: weekStartMs + WEEK_MS > nowMs,
    });
  }
  return result;
}

/**
 * A path straight through the points, in order.
 *
 * Deliberately NOT a smooth curve. This drew a Catmull-Rom spline until
 * 2026-09-16, which overshoots: between two readings the curve can bulge
 * past both of them, so the chart showed a weight that was never recorded —
 * a peak on a line whose whole job is to report what the scale said. A
 * chart may simplify, but it may not invent.
 *
 * Every pixel of this path now lies on a segment between two real readings.
 * Needs at least two points; the caller is expected to check.
 */
export function linearPath(points: readonly { x: number; y: number }[]): string {
  if (points.length < 2) return '';
  const first = points[0];
  if (!first) return '';
  let d = `M ${first.x} ${first.y}`;
  for (let i = 1; i < points.length; i++) {
    const p = points[i];
    if (!p) continue;
    d += ` L ${p.x} ${p.y}`;
  }
  return d;
}

/** Where one week's change bar sits on the axis. */
export interface ChangeBarSpan {
  /** The bar's LEFT edge, in the same pixel space as the point's x. */
  left: number;
  width: number;
}

/**
 * A change bar is centred on its own point, never anchored to its left.
 *
 * The bar and the line point above it describe the same week. Drawing the bar
 * from the point rightwards puts most of its area under the NEXT week at any
 * realistic zoom — at the default six-month range a week is roughly 20px wide
 * and the bar nearly as much — so it reads as the wrong week, disagrees with
 * the crosshair, and pushes the most recent week's bar past the right edge of
 * the plot. Centring is what keeps a bar inside its own point's hit region.
 *
 * `pixelsPerWeek` is how much axis one week actually spans at the current
 * width; `maxWidth` caps a short history from drawing a week as a wall.
 */
export function changeBarSpan(
  centerX: number,
  pixelsPerWeek: number,
  maxWidth: number,
  gap: number,
): ChangeBarSpan {
  const available = pixelsPerWeek > 0 ? pixelsPerWeek - gap : maxWidth;
  const width = Math.min(maxWidth, Math.max(2, available));
  return { left: centerX - width / 2, width };
}

/**
 * The ISO-8601 week number a date falls in.
 *
 * Worth a real function rather than dividing the day-of-year by seven,
 * because the rule is not "the seventh day starts a new week" but "week 1 is
 * the one containing the first Thursday of the year". That makes the turn of
 * the year the whole difficulty: 2019-12-30 is a Monday in week 1 of 2020,
 * and 2021-01-01 is a Friday in week 53 of 2020. A chart labelling either of
 * those "week 1" beside a bar from the other year would be quietly wrong in
 * the one place a reader is most likely to be counting carefully.
 *
 * Everything is done in UTC, matching how the server buckets weeks
 * (Monday-start, UTC) — a local-time reading would drift a bar into the
 * neighbouring week for anyone east or west of Greenwich.
 */
export function isoWeekNumber(iso: string): number | null {
  const parsed = new Date(iso);
  if (Number.isNaN(parsed.getTime())) return null;

  const date = new Date(
    Date.UTC(parsed.getUTCFullYear(), parsed.getUTCMonth(), parsed.getUTCDate()),
  );
  // Monday is 1 and Sunday is 7 here, not JavaScript's Sunday-is-0.
  const weekday = date.getUTCDay() || 7;
  // Step to the Thursday of this week: the year that Thursday falls in is the
  // week's year, which is exactly what the turn-of-year cases hinge on.
  date.setUTCDate(date.getUTCDate() + 4 - weekday);
  const yearStart = Date.UTC(date.getUTCFullYear(), 0, 1);
  return Math.ceil(((date.getTime() - yearStart) / 86_400_000 + 1) / 7);
}

/**
 * How a week is named in a read-out: "week 53", the way a week is named in
 * the Netherlands and by ISO-8601 alike.
 *
 * One number, including across the turn of the year. Mon 28 Dec 2020 to Sun
 * 3 Jan 2021 is week 53, entire — there is no second number to print beside
 * it, and a "53-1" would be a figure the calendar does not contain. Which
 * year's week 53 it is never needs spelling out here, because the dates the
 * change was measured between sit directly beside the label and say so.
 */
export function formatWeekLabel(weekStart: string): string | null {
  const week = isoWeekNumber(weekStart);
  return week === null ? null : `week ${week}`;
}

// ---------------------------------------------------------------------------
// The explicit window — what span the x-axis actually draws
// ---------------------------------------------------------------------------

/**
 * The time span the x-axis draws against: the screen's explicit `window`
 * when it gave one, otherwise (exactly as before a window existed) the
 * data's own first and last point.
 *
 * This exists because the data-derived range lies the moment a window is
 * sparse — asked to show three months of a stretch that only holds three
 * weeks of readings, the old `[min(times), max(times)]` stretched those
 * three weeks across the whole plot, and every pan fraction computed
 * against that axis compounded the error. A window holding no points at
 * all still produces its own real range here, never the `[0, 1]` placeholder
 * a truly windowless empty chart falls back to.
 */
export function resolveTimeRange(
  points: readonly Pick<SeriesPoint, 'recordedAt'>[],
  window?: { from: string; to: string },
): [number, number] {
  if (window) {
    const from = Date.parse(window.from);
    const to = Date.parse(window.to);
    if (Number.isFinite(from) && Number.isFinite(to)) {
      return from <= to ? [from, to] : [to, from];
    }
  }
  return timeDomain(points);
}

// ---------------------------------------------------------------------------
// Panning — a pixel delta turned into a fraction of the visible window
// ---------------------------------------------------------------------------

/**
 * A trackpad or wheel delta, pixel-normalised.
 *
 * `deltaMode` is not the same across browsers or input devices: `0`
 * (`DOM_DELTA_PIXEL`) is already pixels, `1` (`DOM_DELTA_LINE`) counts lines
 * of text, and `2` (`DOM_DELTA_PAGE`) counts whole pages — a line-mode delta
 * of 3 is not 3 pixels, and treating it as one would make a trackpad swipe
 * pan a hundredth of what a mouse-wheel click does. The two multipliers are
 * plain approximations (a browser's own default line-height, a reasonable
 * desktop viewport width) rather than measured from the DOM, matching the
 * same "pure function, no DOM" discipline the rest of this file holds to.
 */
const WHEEL_LINE_PX = 16;
const WHEEL_PAGE_PX = 800;
